import React, { useState } from 'react';
import { useServiceWorker } from '../../hooks/use-service-worker';

export const ServiceWorkerStatus: React.FC = () => {
  const {
    isInitialized,
    isUpdateAvailable,
    currentVersion,
    isLoading,
    error,
    checkForUpdates,
    applyUpdate,
    clearCache,
    getCacheInfo,
    resetError
  } = useServiceWorker();

  // Check if running in WebView
  const isWebView = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    return userAgent.includes('utilitytool') || // Utility Tool WebView
           userAgent.includes('wv') || // Android WebView
           (userAgent.includes('mobile') && userAgent.includes('safari') && !userAgent.includes('chrome')) || // iOS WebView
           userAgent.includes('electron') || // Electron WebView
           userAgent.includes('cordova') || // Cordova/PhoneGap
           userAgent.includes('capacitor') || // Capacitor
           userAgent.includes('react-native') || // React Native WebView
           (window as any).ReactNativeWebView !== undefined; // React Native WebView global
  };

  const [cacheInfo, setCacheInfo] = useState<any[]>([]);
  const [showCacheInfo, setShowCacheInfo] = useState(false);

  const handleCheckUpdates = async () => {
    try {
      await checkForUpdates();
    } catch (error) {
      console.error('Update check failed:', error);
    }
  };

  const handleApplyUpdate = async () => {
    try {
      await applyUpdate();
    } catch (error) {
      console.error('Update application failed:', error);
    }
  };

  const handleClearCache = async () => {
    try {
      await clearCache();
    } catch (error) {
      console.error('Cache clearing failed:', error);
    }
  };

  const handleGetCacheInfo = async () => {
    try {
      const info = await getCacheInfo();
      setCacheInfo(info);
      setShowCacheInfo(true);
    } catch (error) {
      console.error('Failed to get cache info:', error);
    }
  };

  const handleCloseCacheInfo = () => {
    setShowCacheInfo(false);
    setCacheInfo([]);
  };

  if (!isInitialized) {
    return (
      <div className="service-worker-status">
        <div className="status-card">
          <h3>🔄 Service Worker Status</h3>
          <div className="status-info">
            <p><strong>Status:</strong> Initializing...</p>
            {isLoading && <div className="loading-spinner">Loading...</div>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="service-worker-status">
      <div className="status-card">
        <h3>🔄 Service Worker Status</h3>
        
        <div className="status-info">
          {isWebView() && (
            <div className="webview-notice" style={{
              background: '#fff3cd',
              border: '1px solid #ffeaa7',
              borderRadius: '4px',
              padding: '8px 12px',
              marginBottom: '12px',
              color: '#856404'
            }}>
              🚫 <strong>WebView Mode</strong> - Service Worker disabled (updates handled by native app)
            </div>
          )}
          <p><strong>Status:</strong> 
            {isWebView() ? (
              <span className="status-webview">WebView (No SW)</span>
            ) : (
              <span className="status-active">Active</span>
            )}
          </p>
          <p><strong>Current Version:</strong> {currentVersion || 'Unknown'}</p>
          <p><strong>Update Available:</strong> 
            {isWebView() ? (
              <span className="status-webview">Native App Update</span>
            ) : (
              isUpdateAvailable ? (
                <span className="status-update-available">Yes</span>
              ) : (
                <span className="status-no-update">No</span>
              )
            )}
          </p>
        </div>

        {error && (
          <div className="error-message">
            <p><strong>Error:</strong> {error}</p>
            <button 
              onClick={resetError}
              className="btn btn-sm btn-outline-secondary"
            >
              Dismiss
            </button>
          </div>
        )}

        <div className="actions">
          {isWebView() ? (
            <div className="webview-actions" style={{
              background: '#f8f9fa',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              padding: '12px',
              textAlign: 'center',
              color: '#6c757d'
            }}>
              <p>🚫 <strong>WebView Mode</strong></p>
              <p>Service Worker actions are disabled in WebView.</p>
              <p>Updates are handled by the native app.</p>
            </div>
          ) : (
            <>
              <button 
                onClick={handleCheckUpdates}
                className="btn btn-primary"
                disabled={isLoading}
              >
                {isLoading ? 'Checking...' : 'Check for Updates'}
              </button>

              {isUpdateAvailable && (
                <button 
                  onClick={handleApplyUpdate}
                  className="btn btn-success"
                  disabled={isLoading}
                >
                  Apply Update
                </button>
              )}

              <button 
                onClick={handleClearCache}
                className="btn btn-warning"
                disabled={isLoading}
              >
                Clear Cache
              </button>

              <button 
                onClick={handleGetCacheInfo}
                className="btn btn-info"
                disabled={isLoading}
              >
                Cache Info
              </button>

              <button 
                onClick={async () => {
                  try {
                    await window.serviceWorkerManager?.forceUpdate();
                  } catch (error) {
                    console.error('Force update failed:', error);
                  }
                }}
                className="btn btn-danger"
                disabled={isLoading}
                title="Force update by clearing all caches and reloading"
              >
                🚀 Force Update
              </button>

              <button 
                onClick={() => {
                  try {
                    window.serviceWorkerManager?.clearUpdateState();
                    // Force a re-render to check update status
                    window.location.reload();
                  } catch (error) {
                    console.error('Clear update state failed:', error);
                  }
                }}
                className="btn btn-outline-warning"
                disabled={isLoading}
                title="Clear update state and reload (for debugging)"
              >
                🔧 Clear Update State
              </button>

              <button 
                onClick={async () => {
                  try {
                    const isResponsive = await window.serviceWorkerManager?.isServiceWorkerResponsive();
                    window.alert(`Service Worker Responsive: ${isResponsive ? '✅ Yes' : '❌ No'}`);
                  } catch (error) {
                    console.error('Responsiveness check failed:', error);
                    window.alert('Responsiveness check failed');
                  }
                }}
                className="btn btn-outline-info"
                disabled={isLoading}
                title="Test if service worker responds to messages"
              >
                🧪 Test SW Response
              </button>

              <button 
                onClick={async () => {
                  try {
                    if (window.confirm('This will force reload the service worker and clear all caches. Continue?')) {
                      await window.serviceWorkerManager?.forceReloadServiceWorker();
                    }
                  } catch (error) {
                    console.error('Force reload failed:', error);
                    alert('Force reload failed');
                  }
                }}
                className="btn btn-outline-danger"
                disabled={isLoading}
                title="Force reload service worker and clear all caches"
              >
                🔄 Force Reload SW
              </button>
            </>
          )}
        </div>

        {!isWebView() && isUpdateAvailable && (
          <div className="update-notification">
            <div className="alert alert-info">
              <strong>🆕 New version available!</strong>
              <p>Click "Apply Update" to install the latest version.</p>
            </div>
          </div>
        )}

        {showCacheInfo && (
          <div className="cache-info-modal">
            <div className="modal-content">
              <div className="modal-header">
                <h4>Cache Information</h4>
                <button 
                  onClick={handleCloseCacheInfo}
                  className="btn-close"
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                {cacheInfo.length > 0 ? (
                  <div className="cache-list">
                    {cacheInfo.map((cache, index) => (
                      <div key={index} className="cache-item">
                        <strong>{cache.name}:</strong> {cache.size} entries
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>No cache information available</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
